import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DataTable, type Column } from '../components/DataTable';
import { Button, ErrorBox, Field, Input, Modal, PageHeader, Pill, SearchInput, Select, Spinner, type PillColor } from '../components/ui';
import { fetchAll } from '../lib/fetchAll';
import { ORG_TYPE_LABEL } from '../lib/format';
import { normalizeWebsite, websiteDomain } from '../lib/import/normalize';
import { supabase, unwrap } from '../lib/supabase';

async function loadCompanies() {
  return fetchAll((from, to) =>
    supabase
      .from('organizations')
      .select(
        'id, name, org_type, website, main_phone, tags, needs_review, practice_profiles(tier, segment, specialties, source_runs, latest_source_run, flags, num_physicians, total_locations, not_in_latest_run), org_locations(count), contacts(count)',
      )
      .order('name')
      .range(from, to),
  );
}
type Row = Awaited<ReturnType<typeof loadCompanies>>[number];

export const TIER_COLOR: Record<string, PillColor> = { A: 'green', B: 'teal', C: 'blue', D: 'gray', F: 'gray' };

function profile(r: Row) {
  return Array.isArray(r.practice_profiles) ? r.practice_profiles[0] : r.practice_profiles;
}
function count(x: { count: number }[] | null | undefined): number {
  return x?.[0]?.count ?? 0;
}

export function Companies() {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ['companies'], queryFn: loadCompanies });
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [tier, setTier] = useState('');
  const [segment, setSegment] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [run, setRun] = useState('');
  const [flag, setFlag] = useState('');
  const [loc, setLoc] = useState('');
  const [creating, setCreating] = useState(false);

  const options = useMemo(() => {
    const p = (data ?? []).map(profile).filter(Boolean);
    const uniq = (xs: (string | null | undefined)[]) => [...new Set(xs.filter(Boolean) as string[])].sort();
    return {
      segments: uniq(p.map((x) => x!.segment)),
      specialties: uniq(p.flatMap((x) => x!.specialties)),
      runs: uniq(p.flatMap((x) => x!.source_runs)),
      flags: uniq(p.flatMap((x) => x!.flags)),
    };
  }, [data]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data ?? []).filter((r) => {
      const p = profile(r);
      if (type === 'practice' ? !p : type && r.org_type !== type) return false;
      if (tier && p?.tier !== tier) return false;
      if (segment && p?.segment !== segment) return false;
      if (specialty && !p?.specialties.includes(specialty)) return false;
      if (run && !p?.source_runs.includes(run)) return false;
      if (flag && !p?.flags.includes(flag)) return false;
      if (loc === 'yes' && count(r.org_locations) === 0) return false;
      if (loc === 'no' && count(r.org_locations) > 0) return false;
      if (loc === 'dropped' && !p?.not_in_latest_run) return false;
      return !needle || r.name.toLowerCase().includes(needle) || (r.website ?? '').toLowerCase().includes(needle);
    });
  }, [data, q, type, tier, segment, specialty, run, flag, loc]);

  const columns: Column<Row>[] = [
    {
      key: 'name',
      header: 'Company',
      sortValue: (r) => r.name.toLowerCase(),
      render: (r) => (
        <div>
          <span className="font-medium text-slate-900">{r.name}</span>
          {profile(r)?.not_in_latest_run && (
            <Pill color="orange" className="ml-2" title="Missing from the latest Practice Finder run">
              not in latest run
            </Pill>
          )}
        </div>
      ),
    },
    { key: 'type', header: 'Type', sortValue: (r) => r.org_type, render: (r) => <span className="whitespace-nowrap text-slate-600">{ORG_TYPE_LABEL[r.org_type]}</span> },
    {
      key: 'tier',
      header: 'Tier',
      sortValue: (r) => profile(r)?.tier,
      render: (r) => {
        const t = profile(r)?.tier;
        return t ? <Pill color={TIER_COLOR[t]}>{t}</Pill> : null;
      },
    },
    { key: 'segment', header: 'Segment', sortValue: (r) => profile(r)?.segment, render: (r) => <span className="text-slate-600">{profile(r)?.segment?.replace(/_/g, ' ')}</span> },
    { key: 'docs', header: 'MDs', sortValue: (r) => profile(r)?.num_physicians ?? null, render: (r) => profile(r)?.num_physicians ?? '', className: 'text-right' },
    { key: 'locs', header: 'Locations', sortValue: (r) => count(r.org_locations), render: (r) => count(r.org_locations) || '', className: 'text-right' },
    { key: 'people', header: 'People', sortValue: (r) => count(r.contacts), render: (r) => count(r.contacts) || '', className: 'text-right' },
    {
      key: 'web',
      header: 'Website',
      render: (r) =>
        r.website ? (
          <a href={r.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline" onClick={(e) => e.stopPropagation()}>
            {websiteDomain(r.website) ?? 'link'} <ExternalLink className="h-3 w-3" />
          </a>
        ) : null,
    },
  ];

  const isPractice = type === 'practice';
  return (
    <div>
      <PageHeader
        title="Companies"
        subtitle={data ? `${rows.length} of ${data.length}` : undefined}
        actions={
          <Button variant="primary" onClick={() => setCreating(true)}>
            New company
          </Button>
        }
      />
      <div className="mb-3 flex flex-wrap gap-2">
        <SearchInput value={q} onChange={setQ} placeholder="Filter by name or website…" className="!w-64" />
        <Select value={type} onChange={(e) => setType(e.target.value)} className="!w-44">
          <option value="">All types</option>
          <option value="practice">Practices (Practice Finder)</option>
          {Object.entries(ORG_TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
        {isPractice && (
          <>
            <Select value={tier} onChange={(e) => setTier(e.target.value)} className="!w-28">
              <option value="">Any tier</option>
              {['A', 'B', 'C', 'D', 'F'].map((t) => (
                <option key={t}>{t}</option>
              ))}
            </Select>
            <Select value={segment} onChange={(e) => setSegment(e.target.value)} className="!w-40">
              <option value="">Any segment</option>
              {options.segments.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
            <Select value={specialty} onChange={(e) => setSpecialty(e.target.value)} className="!w-36">
              <option value="">Any specialty</option>
              {options.specialties.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
            <Select value={run} onChange={(e) => setRun(e.target.value)} className="!w-36">
              <option value="">Any run</option>
              {options.runs.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
            <Select value={flag} onChange={(e) => setFlag(e.target.value)} className="!w-36">
              <option value="">Any flag</option>
              {options.flags.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </Select>
            <Select value={loc} onChange={(e) => setLoc(e.target.value)} className="!w-44">
              <option value="">Location: any</option>
              <option value="yes">Has a location</option>
              <option value="no">No location</option>
              <option value="dropped">Not in latest run</option>
            </Select>
          </>
        )}
      </div>
      <ErrorBox error={error} />
      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable rows={rows} columns={columns} rowKey={(r) => r.id} onRowClick={(r) => navigate(`/companies/${r.id}`)} initialSort={{ key: 'name', dir: 'asc' }} />
      )}
      <Modal title="New company" open={creating} onClose={() => setCreating(false)}>
        <CompanyForm onDone={(id) => navigate(`/companies/${id}`)} />
      </Modal>
    </div>
  );
}

export function CompanyForm({
  company,
  onDone,
}: {
  company?: { id: string; name: string; org_type: string; website: string | null; main_phone: string | null; notes: string | null; tags: string[] };
  onDone: (id: string) => void;
}) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: company?.name ?? '',
    org_type: company?.org_type ?? 'other',
    website: company?.website ?? '',
    main_phone: company?.main_phone ?? '',
    notes: company?.notes ?? '',
    tags: (company?.tags ?? []).join(', '),
  });
  const save = useMutation({
    mutationFn: async () => {
      if (!f.name.trim()) throw new Error('Enter a name.');
      const website = normalizeWebsite(f.website);
      const row = {
        name: f.name.trim(),
        org_type: f.org_type,
        website,
        website_domain: websiteDomain(website),
        main_phone: f.main_phone.trim() || null,
        notes: f.notes.trim() || null,
        tags: f.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (company) {
        unwrap(await supabase.from('organizations').update(row).eq('id', company.id));
        return company.id;
      }
      return unwrap(await supabase.from('organizations').insert(row).select('id').single()).id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['companies'] });
      qc.invalidateQueries({ queryKey: ['company', id] });
      onDone(id);
    },
  });
  return (
    <div className="space-y-3">
      <Field label="Name">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
      </Field>
      <Field label="Type">
        <Select value={f.org_type} onChange={(e) => setF({ ...f, org_type: e.target.value })}>
          {Object.entries(ORG_TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </Select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Website">
          <Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="example.com" />
        </Field>
        <Field label="Main phone">
          <Input value={f.main_phone} onChange={(e) => setF({ ...f, main_phone: e.target.value })} />
        </Field>
      </div>
      <Field label="Tags" hint="Comma-separated">
        <Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} />
      </Field>
      <Field label="Notes">
        <textarea
          rows={4}
          className="w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          value={f.notes}
          onChange={(e) => setF({ ...f, notes: e.target.value })}
        />
      </Field>
      <ErrorBox error={save.error} />
      <div className="flex justify-end">
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
          {company ? 'Save' : 'Create company'}
        </Button>
      </div>
    </div>
  );
}
